import { useEffect, useMemo, useRef, useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  AttachFile,
  DeleteOutline,
  InsertDriveFile,
  Search,
} from "@material-ui/icons";
import MOModalWrapper from "../../../../components/commons/MOModalWrapper";
import MOButtonV2 from "../../../../components/commons/MOButtonV2";
import { CS_EVENT_TYPE_OPTIONS } from "../constants";
import {
  ACCEPTED_UPLOAD_TYPES,
  buildCreateCustomerServiceEventPayload,
  buildEnclosureFromUpload,
  buildUploadFileCtripPayload,
  fileToBase64,
  formatFileSize,
  generateVendorEventId,
  getThunkErrorMessage,
  isSupportedUploadFile,
  isThunkError,
} from "../ctripReplyHelpers";
import ThunksRateModule from "../../../../redux/actions/thunks/rateModule";
import { toastMessage } from "../../../../helpers/toastMessage";
import "./complaintManagement.css";

const CONTENT_MAX = 2000;

/** Normalize get-ctrip-booking API response into form/display fields */
const mapCtripBooking = (apiBooking) => {
  if (!apiBooking || typeof apiBooking !== "object") return null;

  const booking =
    apiBooking.data && typeof apiBooking.data === "object" && !apiBooking.id
      ? apiBooking.data
      : apiBooking;

  if (!booking?.id && !booking?.bookingReference && !booking?.customerRef) {
    return null;
  }

  return {
    id: booking.id,
    bookingId: booking.bookingReference || booking.id || "",
    guestName: booking.guestName || "",
    status: booking.bookingStatus || booking.status || "",
    ctripPurchaseOrderId: String(
      booking.customerRef || booking.refId || ""
    ),
    vendorOrderId: booking.bookingReference || "",
    serviceProviderId: String(booking.bookingIntegration?.clientId || ""),
    raw: booking,
  };
};

const createEventValidationSchema = Yup.object({
  ctripPurchaseOrderId: Yup.string()
    .trim()
    .required("Ctrip Purchase Order ID is required")
    .matches(/^\d+$/, "Must be a valid number"),
  vendorOrderId: Yup.string().trim().required("Vendor Order ID is required"),
  eventTypeId: Yup.string().required("Event Type is required"),
  operator: Yup.string().trim().required("Operator is required"),
  content: Yup.string()
    .trim()
    .required("Content is required")
    .max(CONTENT_MAX, `Content must be at most ${CONTENT_MAX} characters`),
  enclosure: Yup.array().of(
    Yup.object({
      fileName: Yup.string().required(),
      fileType: Yup.string().required(),
      fileUrl: Yup.string().required(),
    })
  ),
});

const CreateCustomerServiceEventModal = ({
  open,
  onClose,
  onSuccess,
  eventTypeOptions = CS_EVENT_TYPE_OPTIONS,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userInfo.user);
  const fileInputRef = useRef(null);

  const [bookingId, setBookingId] = useState("");
  const [booking, setBooking] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) {
      setBookingId("");
      setBooking(null);
      setSearching(false);
      setSearchError("");
      setUploading(false);
    }
  }, [open]);

  const initialValues = useMemo(
    () => ({
      ctripPurchaseOrderId: booking?.ctripPurchaseOrderId || "",
      vendorOrderId: booking?.vendorOrderId || "",
      serviceProviderId: booking?.serviceProviderId
        ? String(booking.serviceProviderId)
        : "",
      eventTypeId: "",
      vendorEventId: generateVendorEventId(),
      operator: user?.email || user?.fullName || "",
      content: "",
      enclosure: [],
    }),
    [booking, user]
  );

  const handleSearchBooking = async (e) => {
    e?.preventDefault?.();
    const value = bookingId.trim();
    if (!value) {
      setSearchError("Please enter a Booking ID");
      return;
    }

    try {
      setSearching(true);
      setSearchError("");
      const response = await dispatch(
        ThunksRateModule.getCtripBookingsThunk(value)
      );

      if (isThunkError(response)) {
        throw new Error(
          getThunkErrorMessage(response, "Booking not found")
        );
      }

      const mapped = mapCtripBooking(response);
      if (!mapped) {
        throw new Error(
          response?.message || "Booking not found"
        );
      }

      setBooking(mapped);
      toastMessage("success", "Booking found successfully");
    } catch (error) {
      setBooking(null);
      setSearchError(error?.message || "Failed to search booking");
      toastMessage("error", error?.message || "Failed to search booking");
    } finally {
      setSearching(false);
    }
  };

  const handleChangeBooking = () => {
    setBooking(null);
    setSearchError("");
  };

  const handleUploadFiles = async (files, enclosure, setFieldValue) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;

    const invalid = selected.find((file) => !isSupportedUploadFile(file.name));
    if (invalid) {
      toastMessage("error", `Unsupported file type: ${invalid.name}`);
      return;
    }

    try {
      setUploading(true);
      const uploaded = [];

      for (const file of selected) {
        const base64 = await fileToBase64(file);
        const payload = buildUploadFileCtripPayload(file, base64);
        const response = await dispatch(
          ThunksRateModule.uploadFileCtripThunk(payload)
        );

        if (isThunkError(response)) {
          throw new Error(
            getThunkErrorMessage(response, `Failed to upload ${file.name}`)
          );
        }

        if (!response?.url) {
          throw new Error(response?.message || `Failed to upload ${file.name}`);
        }

        uploaded.push({
          ...buildEnclosureFromUpload(response, file.name),
          fileSize: file.size,
        });
      }

      setFieldValue("enclosure", [...enclosure, ...uploaded]);
    } catch (error) {
      toastMessage("error", error?.message || "File upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = buildCreateCustomerServiceEventPayload(values);
      const response = await dispatch(
        ThunksRateModule.createEventCtripThunk(booking.id, payload)
      );

      if (isThunkError(response)) {
        throw new Error(
          getThunkErrorMessage(response, "Failed to create event")
        );
      }

      if (response?.msgCode && response.msgCode !== "OK") {
        throw new Error(response?.message || "Failed to create event");
      }

      toastMessage(
        "success",
        response?.message || "Customer service event created successfully"
      );
      resetForm();
      setBooking(null);
      setBookingId("");
      onSuccess?.();
      onClose();
    } catch (error) {
      toastMessage("error", error?.message || "Failed to create event");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setBooking(null);
    setBookingId("");
    setSearchError("");
    onClose();
  };

  return (
    <MOModalWrapper
      modalOpen={open}
      setModalOpen={(value) => {
        if (!value) handleClose();
      }}
      title="Create Customer Service Event"
      className="cmp-create-event-modal"
    >
      <div className="cmp-create-event-form p-20">
        {/* Step 1: Search booking */}
        <section className="cmp-create-section">
          <h4 className="cmp-section-title">Search Booking</h4>
          <form className="cmp-booking-search" onSubmit={handleSearchBooking}>
            <div className="cmp-filter-field cmp-booking-search-field">
              <label>Booking ID *</label>
              <input
                type="text"
                value={bookingId}
                onChange={(e) => {
                  setBookingId(e.target.value);
                  if (searchError) setSearchError("");
                }}
                placeholder="Enter Booking / Order ID"
                disabled={searching || Boolean(booking)}
              />
              {searchError && (
                <div className="error-message-formik">{searchError}</div>
              )}
              <p className="cmp-helper-text">
                Search by Booking Reference or Ctrip Order ID first.
              </p>
            </div>
            <div className="cmp-booking-search-actions">
              {!booking ? (
                <MOButtonV2
                  text={searching ? "Searching..." : "Search"}
                  type="submit"
                  textColor="#ffffff"
                  backgroundColor="#29AEE6"
                  height={34}
                  fontSize={12}
                  padding="6px 20px"
                  radius={4}
                  loading={searching}
                  disabled={searching || !bookingId.trim()}
                  IconType={() => (
                    <Search style={{ fontSize: 16, marginRight: 6 }} />
                  )}
                />
              ) : (
                <MOButtonV2
                  text="Change Booking"
                  type="button"
                  textColor="#374151"
                  backgroundColor="#F3F4F6"
                  height={34}
                  fontSize={12}
                  padding="6px 16px"
                  radius={4}
                  onClick={handleChangeBooking}
                />
              )}
            </div>
          </form>

          {booking && (
            <div className="cmp-booking-result">
              <div className="cmp-info-grid">
                <div className="cmp-info-row">
                  <span className="cmp-info-label">Booking ID</span>
                  <span className="cmp-info-value">{booking.bookingId}</span>
                </div>
                <div className="cmp-info-row">
                  <span className="cmp-info-label">Guest</span>
                  <span className="cmp-info-value">
                    {booking.guestName || "-"}
                  </span>
                </div>
                <div className="cmp-info-row">
                  <span className="cmp-info-label">Vendor Order</span>
                  <span className="cmp-info-value">{booking.vendorOrderId}</span>
                </div>
                <div className="cmp-info-row">
                  <span className="cmp-info-label">Status</span>
                  <span className="cmp-info-value">{booking.status || "-"}</span>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Step 2: Create form (only after successful search) */}
        {booking && (
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={createEventValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, isSubmitting, resetForm }) => (
              <Form>
                <section className="cmp-create-section">
                  <h4 className="cmp-section-title">Order Information</h4>
                  <div className="cmp-create-grid">
                    <div className="cmp-filter-field">
                      <label>Ctrip Purchase Order ID *</label>
                      <Field
                        name="ctripPurchaseOrderId"
                        type="text"
                        readOnly
                        disabled
                        className="cmp-field-readonly"
                      />
                    </div>
                    <div className="cmp-filter-field">
                      <label>Vendor Order ID *</label>
                      <Field
                        name="vendorOrderId"
                        type="text"
                        readOnly
                        disabled
                        className="cmp-field-readonly"
                      />
                    </div>
                  </div>
                </section>

                <section className="cmp-create-section">
                  <h4 className="cmp-section-title">Event Information</h4>
                  <div className="cmp-create-grid">
                    <div className="cmp-filter-field">
                      <label>Event Type *</label>
                      <Field as="select" name="eventTypeId">
                        <option value="">Select event type</option>
                        {eventTypeOptions.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name_en}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="eventTypeId"
                        component="div"
                        className="error-message-formik"
                      />
                    </div>
                    <div className="cmp-filter-field">
                      <label>Operator *</label>
                      <Field
                        name="operator"
                        type="text"
                        placeholder="Support Agent Name"
                      />
                      <ErrorMessage
                        name="operator"
                        component="div"
                        className="error-message-formik"
                      />
                    </div>
                  </div>
                </section>

                <section className="cmp-create-section">
                  <h4 className="cmp-section-title">Event Description</h4>
                  <div className="cmp-filter-field">
                    <label>Content *</label>
                    <Field
                      as="textarea"
                      name="content"
                      className="cmp-textarea"
                      rows={7}
                      maxLength={CONTENT_MAX}
                      placeholder="Describe the customer service issue in detail..."
                      style={{ minHeight: 150 }}
                    />
                    <div className="cmp-char-counter">
                      {values.content.length} / {CONTENT_MAX}
                    </div>
                    <ErrorMessage
                      name="content"
                      component="div"
                      className="error-message-formik"
                    />
                  </div>
                </section>

                <section className="cmp-create-section">
                  <h4 className="cmp-section-title">Attachments</h4>
                  <div className="cmp-file-upload">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={ACCEPTED_UPLOAD_TYPES}
                      style={{ display: "none" }}
                      onChange={(e) =>
                        handleUploadFiles(
                          e.target.files,
                          values.enclosure,
                          setFieldValue
                        )
                      }
                    />
                    <MOButtonV2
                      text={uploading ? "Uploading..." : "Choose Files"}
                      type="button"
                      textColor="#374151"
                      backgroundColor="#F3F4F6"
                      height={34}
                      fontSize={12}
                      padding="6px 16px"
                      radius={4}
                      disabled={uploading || isSubmitting}
                      IconType={() => (
                        <AttachFile style={{ fontSize: 16, marginRight: 6 }} />
                      )}
                      onClick={() => fileInputRef.current?.click()}
                    />
                    <p className="cmp-helper-text">
                      Optional. Supported: jpg, png, pdf, doc, xlsx, zip, and
                      more.
                    </p>
                  </div>

                  {values.enclosure?.length > 0 && (
                    <div className="cmp-upload-list">
                      {values.enclosure.map((file, index) => (
                        <div
                          key={`${file.fileUrl}-${index}`}
                          className="cmp-upload-item"
                        >
                          <div className="cmp-upload-meta">
                            <InsertDriveFile
                              style={{ fontSize: 18, color: "#6B7280" }}
                            />
                            <div>
                              <p className="m-0 font-13">{file.fileName}</p>
                              <p className="m-0 cmp-helper-text">
                                {file.fileType?.toUpperCase()}
                                {file.fileSize != null
                                  ? ` · ${formatFileSize(file.fileSize)}`
                                  : ""}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="cmp-remove-file-btn"
                            onClick={() =>
                              setFieldValue(
                                "enclosure",
                                values.enclosure.filter((_, i) => i !== index)
                              )
                            }
                          >
                            <DeleteOutline style={{ fontSize: 18 }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <div className="cmp-create-actions">
                  <MOButtonV2
                    text="Cancel"
                    type="button"
                    textColor="#374151"
                    backgroundColor="#F3F4F6"
                    height={36}
                    fontSize={13}
                    padding="8px 20px"
                    radius={4}
                    disabled={isSubmitting || uploading}
                    onClick={handleClose}
                  />
                  <MOButtonV2
                    text="Reset"
                    type="button"
                    textColor="#374151"
                    backgroundColor="#FFFFFF"
                    border="1px solid #D1D5DB"
                    height={36}
                    fontSize={13}
                    padding="8px 20px"
                    radius={4}
                    disabled={isSubmitting || uploading}
                    onClick={() =>
                      resetForm({
                        values: {
                          ...initialValues,
                          vendorEventId: generateVendorEventId(),
                        },
                      })
                    }
                  />
                  <MOButtonV2
                    text="Submit Event"
                    type="submit"
                    textColor="#ffffff"
                    backgroundColor="#29AEE6"
                    height={36}
                    fontSize={13}
                    padding="8px 24px"
                    radius={4}
                    loading={isSubmitting}
                    disabled={isSubmitting || uploading}
                  />
                </div>
              </Form>
            )}
          </Formik>
        )}

        {!booking && (
          <div className="cmp-create-actions">
            <MOButtonV2
              text="Cancel"
              type="button"
              textColor="#374151"
              backgroundColor="#F3F4F6"
              height={36}
              fontSize={13}
              padding="8px 20px"
              radius={4}
              onClick={handleClose}
            />
          </div>
        )}
      </div>
    </MOModalWrapper>
  );
};

export default CreateCustomerServiceEventModal;
